import { WordLadder } from '@minigame-race/contracts';
import { useCallback, useEffect, useState } from 'react';
import type { MinigameProps } from '../../race/types.js';
import { color } from '../../theme.js';

/**
 * Word Ladder.
 *
 * `cat → cot → cog → dog`. The chain grows downward from `start`; `target` sits
 * pinned at the bottom. The row you are typing is local, everything else is the
 * server's authoritative chain.
 *
 * The letter you changed is highlighted lime — the single most useful
 * affordance, because "differs by exactly one letter" is the whole rule.
 */

const REJECTION_TEXT: Record<WordLadder.StepRejection, string> = {
  'not-a-word': 'Not a word in this puzzle',
  'not-one-letter': 'Change exactly one letter',
  'already-visited': 'You have already been here',
  'wrong-length': 'Wrong number of letters',
};

function isLadderView(v: unknown): v is WordLadder.WordLadderPrivateView {
  return typeof v === 'object' && v !== null && (v as { kind?: string }).kind === 'word-ladder';
}

/** Which positions differ between two equal-length words. */
function changedIndices(a: string, b: string): Set<number> {
  const out = new Set<number>();
  for (let i = 0; i < Math.max(a.length, b.length); i++) if (a[i] !== b[i]) out.add(i);
  return out;
}

/** One word rendered as letter tiles. */
function WordRow({
  word,
  previous,
  tone,
  length,
  caretAt,
}: {
  word: string;
  previous?: string | undefined;
  tone: 'start' | 'rung' | 'typing' | 'target';
  length: number;
  caretAt?: number | undefined;
}) {
  const changed = previous ? changedIndices(previous, word) : new Set<number>();

  const base =
    tone === 'start'
      ? { background: 'rgba(255,255,255,.05)', color: 'rgba(234,255,234,.75)', border: '1px solid rgba(234,255,234,.15)' }
      : tone === 'target'
        ? { background: 'rgba(255,176,32,.12)', color: color.amberBright, border: `1px solid rgba(255,176,32,.5)` }
        : tone === 'typing'
          ? { background: 'rgba(166,255,46,.05)', color: color.text, border: `clamp(2px,.5cqmin,3px) solid rgba(166,255,46,.6)` }
          : { background: 'rgba(166,255,46,.10)', color: color.text, border: '1px solid rgba(166,255,46,.25)' };

  const cells = Array.from({ length }, (_, i) => word[i] ?? ' ');

  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${length},1fr)`, gap: 'clamp(3px,.8cqmin,7px)' }}>
      {cells.map((ch, i) => {
        // A changed letter reads lime on a committed rung: it is what you did.
        const isChanged = tone === 'rung' && changed.has(i);
        const isCaret = tone === 'typing' && caretAt === i;
        return (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 'clamp(28px,5cqmin,44px)',
              borderRadius: 'clamp(3px,.7cqmin,6px)',
              fontWeight: 700,
              fontSize: 'clamp(14px,2.9cqmin,26px)',
              textTransform: 'uppercase',
              animation: isCaret ? 'whCur 1s ease-in-out infinite' : undefined,
              ...base,
              ...(isChanged
                ? {
                    background: `linear-gradient(160deg,${color.limeBright},${color.limeDeep})`,
                    color: color.limeInk,
                    border: 'none',
                    boxShadow: '0 0 clamp(5px,1.4cqmin,14px) rgba(166,255,46,.45)',
                  }
                : {}),
            }}
          >
            {isCaret ? (
              <span style={{ width: 'clamp(2px,.5cqmin,3px)', height: 'clamp(14px,2.6cqmin,24px)', background: color.lime, animation: 'caret 1s steps(1,end) infinite' }} />
            ) : (
              ch.trim()
            )}
          </div>
        );
      })}
    </div>
  );
}

export function WordLadderGame({ view, emit, rejectNonce }: MinigameProps) {
  const server = isLadderView(view) ? view : null;
  const [draft, setDraft] = useState('');
  const [shake, setShake] = useState(0);

  const len = server?.wordLength ?? WordLadder.MAX_WORD_LENGTH;
  const chainLen = server?.chain.length ?? 0;

  // A rejected step shakes and clears the row. Unlike Wordle we always clear,
  // because a rejected rung is never a partial word worth keeping.
  useEffect(() => {
    if (rejectNonce === 0) return;
    setShake((s) => s + 1);
    setDraft('');
  }, [rejectNonce]);

  // The server accepted a rung (or an undo): the chain changed, so reset input.
  useEffect(() => {
    setDraft('');
  }, [chainLen]);

  const submit = useCallback(() => {
    if (draft.length !== len) {
      setShake((s) => s + 1);
      return;
    }
    emit({ type: 'step', word: draft } satisfies WordLadder.WordLadderStepEvent);
  }, [draft, emit, len]);

  const press = useCallback(
    (key: string) => {
      if (server?.isComplete) return;
      if (key === 'ENTER') submit();
      else if (key === 'BACK') setDraft((d) => d.slice(0, -1));
      else if (/^[a-z]$/.test(key)) setDraft((d) => (d.length < len ? d + key : d));
    },
    [server?.isComplete, submit, len],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Enter') press('ENTER');
      else if (e.key === 'Backspace') press('BACK');
      else if (/^[a-zA-Z]$/.test(e.key)) press(e.key.toLowerCase());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [press]);

  if (!server) {
    return <div style={{ color: color.textFaint, fontSize: 14 }}>Loading ladder…</div>;
  }

  const hint = server.hints[server.hints.length - 1];
  const rejection = server.lastRejection;

  return (
    <div style={{ width: 'min(46cqh,88cqw)', display: 'flex', flexDirection: 'column', gap: 'clamp(7px,1.5cqmin,15px)' }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <span style={{ fontWeight: 700, fontSize: 'clamp(15px,3cqmin,27px)', letterSpacing: '.04em' }}>WORD LADDER</span>
          <span style={{ fontSize: 'clamp(7px,1.3cqmin,11px)', letterSpacing: '.24em', fontWeight: 600, color: 'rgba(166,255,46,.6)', whiteSpace: 'nowrap' }}>
            {WordLadder.RULES_NOTE}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(6px,1.2cqmin,10px)' }}>
          {server.hintsRemaining > 0 && !server.isComplete && (
            <button
              onPointerDown={(e) => {
                e.preventDefault();
                emit({ type: 'hint' } satisfies WordLadder.WordLadderHintEvent);
              }}
              title="Reveals the next word on a shortest path"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'clamp(4px,.9cqmin,7px)',
                padding: 'clamp(4px,.9cqmin,8px) clamp(9px,1.8cqmin,15px)',
                border: '1px dashed rgba(255,176,32,.55)',
                borderRadius: 999,
                background: 'rgba(255,176,32,.08)',
                touchAction: 'manipulation',
              }}
            >
              <span style={{ fontSize: 'clamp(7px,1.3cqmin,11px)', letterSpacing: '.16em', fontWeight: 700, color: color.amberBright }}>💡 HINT</span>
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, fontSize: 'clamp(10px,2.1cqmin,17px)', lineHeight: 1, color: color.amber }}>
                +{Math.round(server.hintPenaltyMs / 1000)}s
              </span>
            </button>
          )}

          {/* Rungs remaining: the server knows the true distance, so this is a
              real number, not an estimate. It turns the puzzle into a search. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'clamp(5px,1.1cqmin,9px)',
              padding: 'clamp(4px,.9cqmin,8px) clamp(9px,1.8cqmin,16px)',
              border: '1px solid rgba(166,255,46,.4)',
              borderRadius: 999,
              background: 'rgba(166,255,46,.08)',
            }}
          >
            <span style={{ fontSize: 'clamp(7px,1.3cqmin,11px)', letterSpacing: '.2em', fontWeight: 600, color: 'rgba(166,255,46,.75)' }}>TO GO</span>
            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, fontSize: 'clamp(14px,3cqmin,24px)', lineHeight: 1, color: color.limeBright }}>
              {server.rungsRemaining}
            </span>
          </div>
        </div>
      </div>

      {/* the chain */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(3px,.8cqmin,7px)', minHeight: 0, overflowY: 'auto' }}>
        {server.chain.map((word, i) => (
          <WordRow
            key={`${word}-${i}`}
            word={word}
            previous={i > 0 ? server.chain[i - 1] : undefined}
            tone={i === 0 ? 'start' : 'rung'}
            length={len}
          />
        ))}

        {/* the row you are typing */}
        {!server.isComplete && (
          <div style={{ animation: shake > 0 ? `shake .3s ease-in-out ${shake}` : undefined }}>
            <WordRow
              word={draft}
              tone="typing"
              length={len}
              caretAt={draft.length < len ? draft.length : undefined}
            />
          </div>
        )}

        {/* the target, pinned */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 'clamp(3px,.8cqmin,7px)' }}>
          <span style={{ fontSize: 'clamp(7px,1.3cqmin,11px)', letterSpacing: '.2em', fontWeight: 700, color: 'rgba(255,176,32,.75)', flex: 'none' }}>
            ↓ TARGET
          </span>
          <div style={{ flex: 1, height: 1, background: 'rgba(255,176,32,.25)' }} />
        </div>
        <WordRow word={server.target} tone="target" length={len} />
      </div>

      {/* controls + feedback */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(6px,1.4cqmin,12px)' }}>
        <button
          onPointerDown={(e) => {
            e.preventDefault();
            emit({ type: 'undo' } satisfies WordLadder.WordLadderUndoEvent);
          }}
          disabled={server.chain.length <= 1 || server.isComplete}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: 'clamp(7px,1.5cqmin,12px) clamp(11px,2.2cqmin,18px)',
            border: '1px solid rgba(234,255,234,.18)',
            borderRadius: 10,
            background: 'rgba(255,255,255,.03)',
            color: 'rgba(234,255,234,.8)',
            fontWeight: 700,
            fontSize: 'clamp(10px,1.9cqmin,15px)',
            letterSpacing: '.06em',
            touchAction: 'manipulation',
          }}
        >
          ↶ UNDO
        </button>

        <div style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
          {hint !== undefined && !server.solved && (
            <span style={{ fontSize: 'clamp(9px,1.7cqmin,14px)', fontWeight: 700, color: color.amberBright, letterSpacing: '.04em' }}>
              💡 TRY <span style={{ fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase' }}>{hint}</span>
            </span>
          )}
          {hint === undefined && rejection !== undefined && (
            <span style={{ fontSize: 'clamp(9px,1.6cqmin,13px)', color: 'rgba(255,176,32,.85)', fontWeight: 600, letterSpacing: '.04em' }}>
              {REJECTION_TEXT[rejection]}
            </span>
          )}
          {hint === undefined && rejection === undefined && !server.solved && (
            <span style={{ fontSize: 'clamp(9px,1.6cqmin,13px)', color: 'rgba(234,255,234,.35)', fontWeight: 600, letterSpacing: '.04em' }}>
              {WordLadder.RULES_HINT}
            </span>
          )}
          {server.solved && (
            <span style={{ fontSize: 'clamp(11px,2cqmin,16px)', fontWeight: 800, color: color.lime, letterSpacing: '.08em' }}>
              ✓ REACHED IN {server.chain.length - 1} RUNGS
            </span>
          )}
        </div>

        <button
          onPointerDown={(e) => {
            e.preventDefault();
            submit();
          }}
          disabled={draft.length !== len || server.isComplete}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: 'clamp(7px,1.5cqmin,12px) clamp(13px,2.6cqmin,22px)',
            borderRadius: 10,
            background: draft.length === len ? `linear-gradient(160deg,${color.limeBright},${color.limeDeep})` : 'rgba(166,255,46,.14)',
            color: draft.length === len ? color.limeInk : color.limeBright,
            fontWeight: 700,
            fontSize: 'clamp(10px,1.9cqmin,15px)',
            letterSpacing: '.06em',
            touchAction: 'manipulation',
          }}
        >
          CLIMB ↑
        </button>
      </div>
    </div>
  );
}
